<script setup lang="ts">
import {
  Scene,
  PerspectiveCamera,
  TextureLoader,
  Object3D,
  AmbientLight,
  WebGLRenderer,
  PMREMGenerator,
  WebGLRenderTarget,
  Group,
  Mesh,
  MeshStandardMaterial,
  Material,
  Light,
} from 'three/src/Three';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls';

import {
  EquirectangularReflectionMapping,
  SRGBColorSpace,
  VSMShadowMap,
} from 'three/src/constants';

import {ref, watch} from 'vue';

const mm3dOutputCanvas = ref<HTMLCanvasElement | undefined>(undefined);

const loaded = ref(false);

const emit = defineEmits(['rendered']);

const replaceableColors = ref<string[]>([]);

const props = withDefaults(
  defineProps<{
    width: number;
    height: number;
    modelResourceUrl: string;
    designAreaIdentifier: string;
    colorReplaceIdentifier: string;
    designFileUrl: string;
    environmentMapUrl: string | undefined;
    environmentMapIntensity: number;
    enableShadows: boolean;
    lights: Light[];
    cameraPosition: {x: number; y: number; z: number};
    cameraTarget: {x: number; y: number; z: number};
    activeColorName?: string;
  }>(),
  {
    width: 500,
    height: 500,
    environmentMapIntensity: 1,
    designAreaIdentifier: 'design_area',
    colorReplaceIdentifier: 'col_replace',
    lights: () => [new AmbientLight(0xffffff, 0.5)],
    cameraPosition: () => ({
      x: 0,
      y: 0,
      z: 10,
    }),
    cameraTarget: () => ({
      x: 0,
      y: 0,
      z: 0,
    }),
    enableShadows: false,
  }
);

let modelGroup: Object3D;
let renderer: WebGLRenderer;
let designArea: Mesh | undefined = undefined;
let controls: OrbitControls;

const envMapLoaded = ref(false);
const modelLoaded = ref(false);
const designFileLoaded = ref(false);

const scene = new Scene();

const camera = new PerspectiveCamera(45, props.width / props.height, 0.1, 1000);
camera.position.set(
  props.cameraPosition.x,
  props.cameraPosition.y,
  props.cameraPosition.z
);
camera.lookAt(props.cameraTarget.x, props.cameraTarget.y, props.cameraTarget.z);

let lightGroup: Group;

const forAllMeshesInScene = (callback: (object: Mesh) => void) => {
  modelGroup.traverse(object => {
    if (object.type === 'Mesh') {
      callback(object as Mesh);
    }
  });
};

const createLights = () => {
  if (lightGroup) {
    scene.remove(lightGroup);
  }

  lightGroup = new Group();
  props.lights.forEach(light => {
    // we have to clone the light to avoid modifying the props
    lightGroup.add(light.clone());
  });

  scene.add(lightGroup);
};

const createRenderer = () => {
  renderer = new WebGLRenderer({
    antialias: true,
    alpha: true,
    canvas: mm3dOutputCanvas.value,
  });
  renderer.shadowMap.enabled = props.enableShadows;
  renderer.shadowMap.type = VSMShadowMap;
  renderer.setSize(props.width, props.height);
};

const createControls = () => {
  controls = new OrbitControls(camera, mm3dOutputCanvas.value);

  controls.maxPolarAngle = Math.PI / 2;
  controls.minPolarAngle = Math.PI / 4;
  controls.enablePan = false;
};

const textureLoader = new TextureLoader();
textureLoader.setCrossOrigin('Anonymous');

const loadDesignFile = () => {
  textureLoader.load(props.designFileUrl, texture => {
    texture.flipY = false;
    texture.colorSpace = SRGBColorSpace;
    if (designArea) {
      const material = designArea.material as Material;

      if (material.type === 'MeshStandardMaterial') {
        const material = designArea.material as MeshStandardMaterial;

        material.map = texture;
        material.transparent = true;
        material.needsUpdate = true;
      }

      designFileLoaded.value = true;
      render();
    }
  });
};

const applyEnvironmentMapToModel = (envMap: WebGLRenderTarget) => {
  forAllMeshesInScene(mesh => {
    const material = mesh.material as MeshStandardMaterial;
    material.envMap = envMap.texture;
    material.envMapIntensity = props.environmentMapIntensity;
    material.needsUpdate = true;
  });
};

const loadEnvironmentMap = () => {
  if (props.environmentMapUrl) {
    textureLoader.load(props.environmentMapUrl, texture => {
      texture.mapping = EquirectangularReflectionMapping;
      texture.colorSpace = SRGBColorSpace;

      const pmremGenerator = new PMREMGenerator(renderer);

      applyEnvironmentMapToModel(pmremGenerator.fromEquirectangular(texture));

      envMapLoaded.value = true;

      render();
    });
  }
};

const findDesignArea = (): Mesh => {
  const designArea = modelGroup.children.find(
    c => c.name === props.designAreaIdentifier
  );

  if (!designArea) {
    throw new Error(`No ${props.designAreaIdentifier} found in 3D model`);
  }

  return designArea as Mesh;
};

const getColorReplaceableObjects = (): Mesh[] =>
  modelGroup.children.filter(child => {
    return Object.keys(child.userData).includes(props.colorReplaceIdentifier);
  }) as Mesh[];

const getReplaceableColors = (): string[] => {
  const replaceableObjects = getColorReplaceableObjects();

  if (!replaceableObjects.length) {
    return [];
  }

  const colorKeys = Object.keys(replaceableObjects[0].userData).filter(
    key => key.startsWith('col_') && key !== props.colorReplaceIdentifier
  );

  return colorKeys.map(key => key.replace('col_', ''));
};

const replaceColors = (targetColor: string) => {
  const replaceableObjects = getColorReplaceableObjects();

  replaceableObjects.forEach(object => {
    const material = object.material as MeshStandardMaterial;
    const materialData = JSON.parse(object.userData[`col_${targetColor}`]);

    if (material.type === 'MeshStandardMaterial') {
      if (materialData.hex) {
        material.color.setHex(parseInt(materialData.hex.replace('#', ''), 16));
      }
      material.metalness = materialData.metalness ?? material.metalness;
      material.roughness = materialData.roughness ?? material.roughness;
      material.needsUpdate = true;
    }
  });
};

const loadInScene = () => {
  const loader = new GLTFLoader();
  loader.load(
    props.modelResourceUrl,
    gltf => {
      modelGroup = gltf.scene;
      scene.add(modelGroup);

      modelLoaded.value = true;

      forAllMeshesInScene(mesh => {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      });

      designArea = findDesignArea();

      if (props.activeColorName) {
        const activeColorName = props.activeColorName
          .toLowerCase()
          .replace(/\W/g, '_');

        replaceableColors.value = getReplaceableColors();

        if (
          replaceableColors.value.length &&
          props.activeColorName &&
          replaceableColors.value.includes(activeColorName)
        ) {
          replaceColors(activeColorName);
        }
      }

      if (designArea) {
        loadDesignFile();
      }

      if (props.environmentMapUrl && renderer) {
        loadEnvironmentMap();
      }

      loaded.value = true;
    },
    undefined,
    error => console.error(error)
  );
};

const animate = () => {
  // only queue up the next frame if the canvas is still mounted
  if (mm3dOutputCanvas.value) {
    requestAnimationFrame(animate);
    render();
  }
};

const render = () => {
  if (
    modelLoaded.value &&
    (envMapLoaded.value || !props.environmentMapUrl) &&
    (designFileLoaded.value || !props.designFileUrl)
  ) {
    renderer?.render(scene, camera);
    emit('rendered', renderer.domElement);
  }
};

watch(mm3dOutputCanvas, canvas => {
  if (canvas) {
    createLights();
    createRenderer();
    createControls();
    loadInScene();
    animate();
  }
});

watch(
  () => [props.environmentMapIntensity, props.environmentMapUrl],
  () => {
    if (modelGroup) {
      loadEnvironmentMap();
    }
  }
);

watch(
  () => props.designFileUrl,
  () => {
    loadDesignFile();
    renderer?.render(scene, camera);
  }
);

watch(
  () => [props.width, props.height],
  () => {
    if (renderer) {
      renderer.setSize(props.width, props.height);
      render();
    }
  }
);

watch(
  () => props.modelResourceUrl,
  () => {
    scene.remove(modelGroup);
    loadInScene();
  }
);

watch(
  () => props.enableShadows,
  () => {
    if (renderer) {
      renderer.shadowMap.enabled = props.enableShadows;

      forAllMeshesInScene(mesh => {
        mesh.castShadow = props.enableShadows;
        mesh.receiveShadow = props.enableShadows;
      });
      render();
    }
  }
);

watch(
  () => props.designAreaIdentifier,
  () => {
    designArea = findDesignArea();
  }
);

watch(
  () => props.lights,
  () => {
    createLights();

    renderer?.render(scene, camera);
  }
);

watch(
  () => [props.cameraPosition, props.cameraTarget],
  () => {
    camera.position.set(
      props.cameraPosition.x,
      props.cameraPosition.y,
      props.cameraPosition.z
    );
    camera.lookAt(
      props.cameraTarget.x,
      props.cameraTarget.y,
      props.cameraTarget.z
    );

    renderer?.render(scene, camera);
  }
);
</script>

<template>
  <tml-loader :loading="!loaded" :loading-ratio="1" />
  <canvas ref="mm3dOutputCanvas" class="render-canvas max-w-full" />
</template>

<style scoped lang="scss">
.render-canvas {
  width: auto !important;
  height: auto !important;
}
</style>
